import { useMemo, useRef, useState, type ReactNode } from "react";
import { VirtualNftGrid } from "./VirtualNftGrid";
import type { InventoryView, NftStatus, NftView } from "../types";
import type { WalletView } from "../../../state/ui-types";
import { ArrowUpRightIcon, HourglassIcon, LeafIcon, RefreshIcon, ShieldCheckIcon } from "../../../components/Icons";
import { explainBlockingReason } from "../../../components/format";
import { nftRewardPercent } from "../../../lib/reward-format";
import { nftMintFee } from "./nft-fee";
import "../styles/nft-fees.css";
import { NftArtwork, nftMediaLabel, nftStateLabel } from "./NftArtwork";
import { nftLabel } from "./nft-label";

type InventoryFilter = "ALL" | "FINALIZED" | "PENDING";

interface NftInventoryProps {
  inventory: InventoryView;
  wallet?: Pick<WalletView, "status" | "address">;
  lookup?: ReactNode;
  onSelect(nft: NftView): void;
  onOpenHistory(): void;
  onLoadMore(): void;
  onRefresh(): void;
}

export function NftInventory({ inventory, wallet, lookup, onSelect, onOpenHistory, onLoadMore, onRefresh }: NftInventoryProps) {
  const [filter, setFilter] = useState<InventoryFilter>("ALL");
  const lastArtworkRefresh = useRef(-Infinity);
  const refreshArtworkMetadata = () => {
    // Several mounted cards can fail together; coalesce their head refreshes.
    if (Date.now() - lastArtworkRefresh.current < 12_000) return;
    lastArtworkRefresh.current = Date.now();
    onRefresh();
  };
  const items = useMemo(() => {
    if (inventory.items === null) return null;
    if (filter === "ALL") return inventory.items.filter((item) => item.status !== "BURNED");
    if (filter === "FINALIZED") return inventory.items.filter((item) => item.status === "FINALIZED");
    return inventory.items.filter((item) => item.status === "PENDING_RANDOMNESS" || item.status === "RANDOMNESS_READY");
  }, [filter, inventory.items]);
  const refreshLabel = inventory.refreshing ? "Checking inventory…" :
    inventory.updateError ? "Retry inventory refresh" : "Update inventory";
  const sessionRequired = wallet && wallet.status !== "CONNECTED";
  const authenticationRequired = wallet?.status === "SESSION_EXPIRED" || wallet?.status === "SIGNING_IN" ||
    (wallet?.status === "DISCONNECTED" && wallet.address !== null);
  const resourceFailure = inventory.blockingReasons.some(reason =>
    reason === "BACKEND_UNAVAILABLE" || reason === "CONTRACT_NOT_DEPLOYED" || reason === "TOKEN_NOT_LAUNCHED");
  const sessionMessage = wallet?.status === "WRONG_NETWORK" ? "Switch to the collection network to view your NFTs" :
    authenticationRequired ? "Sign in with your wallet to view your NFTs" : "Connect your wallet to view your NFTs";
  const previouslyLoaded = (inventory.items?.length ?? 0) > 0;

  return (
    <section className="inventory-section" aria-labelledby="inventory-title" aria-busy={inventory.loading}>
      <div className="section-heading-row">
        <div className="inventory-heading">
          <span className="field-eyebrow">YOUR FOREST RELICS</span>
          <h2 id="inventory-title">MY NFTs</h2>
          <div className="inventory-freshness">
            <p>{sessionRequired && !resourceFailure ? "Wallet session required" : inventory.freshnessLabel}</p>
            {inventory.available ? <button type="button"
              className={`inventory-refresh${inventory.newSnapshot || inventory.updateError ? " has-notice" : ""}`}
              disabled={inventory.refreshing} aria-label={refreshLabel} title={refreshLabel} aria-busy={inventory.refreshing}
              onClick={onRefresh}>
              <RefreshIcon />
            </button> : null}
          </div>
        </div>
        <div className="inventory-tools">
          <div className="filter-tabs" role="group" aria-label="Filter NFTs">
            {(["ALL", "FINALIZED", "PENDING"] as const).map((value) => (
              <button type="button" key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}>{value}</button>
            ))}
          </div>
          <button type="button" className="secondary-action" onClick={onOpenHistory}>Inventory info <ArrowUpRightIcon /></button>
        </div>
      </div>

      {lookup ? <details className="inventory-lookup"><summary>Find an NFT by token ID</summary>{lookup}</details> : null}
      {inventory.updateError && items !== null && !sessionRequired ? <p className="inventory-notice" role="status">
        Could not refresh. {previouslyLoaded ? "Showing previously loaded NFTs." : "Showing the last confirmed empty inventory."} Use Update inventory to retry.
      </p> : inventory.newSnapshot && !sessionRequired ? <p className="inventory-notice" role="status">
        The page cursor expired. Update inventory to continue; loaded NFTs stay visible.
        Load more may revisit earlier pages before reaching new records.
      </p> : null}

      {sessionRequired && !resourceFailure ? (
        <div className="inventory-empty" role="status"><LeafIcon /><div><strong>{sessionMessage}</strong>
          <p>Your personal inventory needs an authenticated wallet session. Public pool data{lookup ? " and NFT lookup remain available." : " remains available."}</p>
        </div></div>
      ) : inventory.loading && items === null ? (
        <div className="inventory-unavailable" role="status"><HourglassIcon /><p>Loading canonical inventory…</p></div>
      ) : !inventory.available || items === null ? (
        <div className="inventory-unavailable">
          <span className="unavailable-rune"><span>!</span></span>
          <div><strong>{inventory.blockingReasons.includes("BACKEND_UNAVAILABLE") ? "BACKEND UNAVAILABLE" :
            inventory.blockingReasons.includes("CONTRACT_NOT_DEPLOYED") ?
              "CONTRACT NOT DEPLOYED" : "Inventory temporarily unavailable"}</strong><p>
            {inventory.blockingReasons.includes("BACKEND_UNAVAILABLE") ?
              "The connected API could not be reached. No demo inventory was substituted." :
              inventory.blockingReasons.includes("CONTRACT_NOT_DEPLOYED") ?
              "The project NFT collection is not deployed on the configured chain." :
              "Canonical ownership data has not been confirmed. No NFTs are being shown as missing or burned."}
          </p>{inventory.blockingReasons.length > 0 ? <p role="status">{inventory.blockingReasons.map(explainBlockingReason).join(" ")}</p> : null}</div>
        </div>
      ) : items.length === 0 ? (
        <div className="inventory-empty">
          <LeafIcon />
          <div><strong>{filter === "ALL" ? previouslyLoaded ? "No active NFTs" : "No NFTs yet" : "No NFTs match this filter"}</strong><p>
            {filter === "ALL" ? previouslyLoaded ? "No unburned NFTs are present in the loaded records." : "NFTs appear here after a successful mint is indexed." :
              "Try another filter or load more NFTs. Filters apply to the records loaded so far."}
          </p></div>
        </div>
      ) : (
        <>
          <VirtualNftGrid key={filter} items={items} onSelect={onSelect} onMetadataMismatch={refreshArtworkMetadata} />
        </>
      )}
      {!sessionRequired && inventory.available && inventory.hasMore ? <button type="button" className="secondary-action inventory-load-more"
        disabled={inventory.loadingMore || inventory.refreshing || inventory.newSnapshot} onClick={onLoadMore}>
        {inventory.loadingMore ? "Loading verified inventory…" : "Load more NFTs"}
      </button> : null}
    </section>
  );
}

interface NftCardProps {
  nft: NftView;
  onOpen(): void;
  onMetadataMismatch?: (() => void) | undefined;
}

export function NftCard({ nft, onOpen, onMetadataMismatch }: NftCardProps) {
  return (
    <article className={`nft-card tier-${nft.tierUsd} status-${nft.status.toLowerCase()}`}>
      <button type="button" className="nft-card-hitbox" onClick={onOpen} aria-label={`Open NFT ${nft.tokenId} details`} />
      <div className="nft-image-frame">
        <NftArtwork nft={nft} lazy onMetadataMismatch={onMetadataMismatch} />
      </div>
      <div className="nft-card-body">
        <div className="nft-title-row">
          <div><span className="nft-tier-name">{nftLabel(nft)}</span><strong>#{nft.tokenId}</strong></div>
          <NftStatusBadge status={nft.status} />
        </div>
        <div className="nft-reward-row">
          <span>REWARD RATE</span>
          <strong>{nftRewardPercent(nft)}</strong>
        </div>
        <div className="nft-fee-row"><span>Mint fee paid</span><strong title={nftMintFee(nft)}>{nftMintFee(nft)}</strong></div>
        <div className="nft-source-row"><span className="token-dot" /> {nft.sourceSymbol}<ArrowUpRightIcon /></div>
        <p className="nft-media-label">{nftMediaLabel(nft)}</p>
      </div>
    </article>
  );
}

function NftStatusBadge({ status }: { status: NftStatus }) {
  const finalized = status === "FINALIZED";
  return (
    <span className={`nft-status ${finalized ? "finalized" : "pending"}`}>
      {finalized ? <ShieldCheckIcon /> : <HourglassIcon />}
      <span className="nft-status-copy" title={nftStateLabel(status)}>{status === "FINALIZED" ? "Finalized" : status === "RANDOMNESS_READY" ? "Ready" : status === "BURNED" ? "Redeemed" : "Waiting"}</span>
    </span>
  );
}
